import type { ReactNode } from "react";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { css } from "../../theme/styled";
import { hasKeyboardFocus } from "../../util/keyboardFocus";

/**
 * Used for wrapping MenuItem. Adds styling and keyboard
 * navigation functionality.
 *
 * Note: Needs children (nested is fine) to have attribute: data-component-type="menu-item"
 * or role="menuitem" to be able to regard it as focusable.
 */
export const MenuList = ({
  children,
  autoFocus = false,
}: {
  children: ReactNode;
  /** Whether to automatically focus the first menu item when the menu is rendered */
  autoFocus?: boolean;
}): React.JSX.Element => {
  const listRef = useRef<HTMLUListElement>(null);
  const [menuItems, setMenuItems] = useState<HTMLElement[]>([]);

  useKeyboardNavigation(menuItems);

  // Get all focusable menu items, even nested ones, to handle focus.
  useLayoutEffect(() => {
    const menuItems: NodeListOf<HTMLElement> | undefined = listRef.current
      ?.querySelectorAll(
        "[data-component-type=menu-item], [role=menuitem]",
      );

    if (!menuItems?.length) {
      return console.warn(
        "No children with attribute data-component-type=menu-item or role=menuitem found.",
      );
    }

    const filteredElements = [...menuItems.values()].filter(
      (item) => item.ariaDisabled !== "true",
    );

    setMenuItems(filteredElements);
  }, [listRef]);

  // Auto focus first menu item if autoFocus is enabled
  useLayoutEffect(() => {
    if (autoFocus && menuItems.length > 0) {
      // Use requestAnimationFrame to ensure the menu is fully rendered
      requestAnimationFrame(() => {
        const firstMenuItem = menuItems[0];
        if (firstMenuItem) {
          firstMenuItem.focus();
        }
      });
    }
  }, [autoFocus, menuItems]);

  return (
    <ul
      ref={listRef}
      data-component-type="menu-list"
      role="menu"
      tabIndex={-1}
      className={css({ padding: ["$spacing-8", 0], margin: 0 })}
    >
      {children}
    </ul>
  );
};

// Handles arrow key navigation. Takes disabled and nested children into account.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function useKeyboardNavigation(elements: HTMLElement[]) {
  const [currentFocusIndex, setCurrentFocusIndex] = useState(0);

  const getNextFocusableIndex = (
    currentIndex: number,
    direction: "up" | "down",
  ): number => {
    if (!elements.length) return -1;

    const getNextIndex = (): number => {
      const nextIndex = direction === "up"
        ? currentIndex - 1
        : currentIndex + 1;

      // Out of bounds bottom
      if (nextIndex >= elements.length) {
        return 0;
        // Out of bounds top
      } else if (nextIndex <= -1) {
        return elements.length - 1;
      }

      return nextIndex;
    };

    const nextIndex = getNextIndex();
    const nextElement = elements[nextIndex];

    // Return next index if exists
    if (nextElement) {
      return nextIndex;
    }

    // Else continue traversing list
    return getNextFocusableIndex(nextIndex, direction);
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!elements.length) return;

      if (e.key === "ArrowDown") {
        // Prevent scroll
        e.preventDefault();

        const focusableIndex = getNextFocusableIndex(currentFocusIndex, "down");
        const focusableElement = elements[focusableIndex];
        setCurrentFocusIndex(focusableIndex);
        focusableElement?.focus();
      } else if (e.key === "ArrowUp") {
        // Prevent scroll
        e.preventDefault();

        const focusableIndex = getNextFocusableIndex(currentFocusIndex, "up");
        const focusableElement = elements[focusableIndex];

        setCurrentFocusIndex(focusableIndex);
        focusableElement?.focus();
      }
    },
    [elements, currentFocusIndex],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, false);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, false);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (!elements.length) return;

    const elementValues = [...elements.values()];
    const autoFocusedIndex = elementValues.findIndex(
      (el) => el === document.activeElement,
    );

    // Focus first element that has autoFocus prop
    if (autoFocusedIndex > -1) {
      setCurrentFocusIndex(autoFocusedIndex);
      return;
    }

    if (hasKeyboardFocus()) {
      // Else set focus on first element
      const focusableElement = elements[currentFocusIndex];
      focusableElement?.focus();
    }
  }, [elements]);

  return [currentFocusIndex, setCurrentFocusIndex];
}
